import { useEffect, useState } from 'react';
import { Image, Link, Send, Trash2, Type, X, XSquare } from 'lucide-react';
import { toast } from 'react-toastify';
import { couponAPI } from '../../../config/coupon-api';
import { whatsappAPI } from '../../../config/whatsapp-api';
import '../WhatsApp/WhatsApp.css';

const emptyForm = {
  templateName: '',
  headerType: 'none',
  headerText: '',
  headerImage: null,
  variables: [],
  buttons: [],
};

const CreateCouponWhatsappQueModal = ({
  isOpen,
  onClose,
  onSuccess,
  couponId,
  eligibleUserCount = 0,
}) => {
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [isMessageModalOpen, setIsMessageModalOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [loadingMoreTemplates, setLoadingMoreTemplates] = useState(false);
  const [nextCursor, setNextCursor] = useState('none');
  const [hasMoreTemplates, setHasMoreTemplates] = useState(true);
  const [selectedTemplateName, setSelectedTemplateName] = useState('');
  const [formData, setFormData] = useState(emptyForm);
  const [sendingQue, setSendingQue] = useState(false);

  const resetState = () => {
    setIsTemplateModalOpen(false);
    setIsMessageModalOpen(false);
    setTemplates([]);
    setLoadingTemplates(false);
    setLoadingMoreTemplates(false);
    setNextCursor('none');
    setHasMoreTemplates(true);
    setSelectedTemplateName('');
    setFormData(emptyForm);
    setSendingQue(false);
  };

  const fetchTemplates = async (cursor = 'none', more = false) => {
    if (more) {
      setLoadingMoreTemplates(true);
    } else {
      setLoadingTemplates(true);
    }

    try {
      const response = await whatsappAPI.fetchTemplates(cursor);

      if (response.success && response.data.templates) {
        if (more) {
          setTemplates((prev) => [...prev, ...response.data.templates]);
        } else {
          setTemplates(response.data.templates);
        }

        if (response.data.nextCursor) {
          setNextCursor(response.data.nextCursor);
          setHasMoreTemplates(true);
        } else {
          setNextCursor(null);
          setHasMoreTemplates(false);
        }
      } else {
        if (!more) setTemplates([]);
        setNextCursor(null);
        setHasMoreTemplates(false);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      if (!error.message?.includes('No more templates')) {
        toast.error('Failed to load templates');
      }
      if (!more) setTemplates([]);
      setHasMoreTemplates(false);
    } finally {
      if (more) {
        setLoadingMoreTemplates(false);
      } else {
        setLoadingTemplates(false);
      }
    }
  };

  useEffect(() => {
    if (!isOpen) {
      resetState();
      return;
    }

    setIsTemplateModalOpen(true);
    setIsMessageModalOpen(false);
    fetchTemplates();
  }, [isOpen]);

  if (!isOpen) return null;

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleLoadMoreTemplates = () => {
    if (nextCursor && nextCursor !== 'none' && hasMoreTemplates && !loadingMoreTemplates) {
      fetchTemplates(nextCursor, true);
    }
  };

  const handleProceedWithTemplate = () => {
    if (!selectedTemplateName) {
      toast.error('Please select a template');
      return;
    }

    const selectedTemplate = templates.find((temp) => temp.name === selectedTemplateName);
    if (selectedTemplate) {
      setFormData({
        ...emptyForm,
        templateName: selectedTemplate.name,
        headerType: selectedTemplate.headerType || 'none',
        variables: Array.from({ length: selectedTemplate.body?.variables || 0 }, (_, i) => ({
          id: Date.now() + i,
          value: '',
        })),
        buttons: selectedTemplate.buttons || [],
      });
    }

    setIsTemplateModalOpen(false);
    setIsMessageModalOpen(true);
  };

  const handleTemplateClick = async () => {
    setIsMessageModalOpen(false);
    setIsTemplateModalOpen(true);
    if (templates.length === 0) {
      await fetchTemplates();
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const updateVariable = (id, value) => {
    setFormData((prev) => ({
      ...prev,
      variables: prev.variables.map((v) => (v.id === id ? { ...v, value } : v)),
    }));
  };

  const updateButton = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      buttons: prev.buttons.map((btn, i) => (i === index ? { ...btn, [field]: value } : btn)),
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 600 * 1024) {
      toast.error('Image size must be less than 600KB');
      return;
    }

    setFormData((prev) => ({
      ...prev,
      headerImage: file,
    }));
  };

  const removeImage = () => {
    setFormData((prev) => ({
      ...prev,
      headerImage: null,
    }));
  };

  const handleSend = async () => {
    if (!formData.templateName) {
      toast.error('Please select a template');
      return;
    }

    if (formData.headerType === 'image' && !formData.headerImage) {
      toast.error('Please upload a header image');
      return;
    }

    if (formData.variables.length > 0) {
      const emptyVariables = formData.variables.filter((v) => !v.value.trim());
      if (emptyVariables.length > 0) {
        toast.error('Please fill all variables');
        return;
      }
    }

    if (formData.buttons.length > 0) {
      const emptyCopyButtons = formData.buttons.filter((btn) => btn.type === 'copy' && !btn.copyText?.trim());
      if (emptyCopyButtons.length > 0) {
        toast.error('Please fill copy text for all copy buttons');
        return;
      }
    }

    setSendingQue(true);

    try {
      const response = await couponAPI.createWhatsappQue(couponId, {
        templateName: formData.templateName,
        headerType: formData.headerType,
        headerText: formData.headerText,
        headerImage: formData.headerImage,
        variables: formData.variables,
        buttons: formData.buttons,
      });

      if (response.success) {
        toast.success('WhatsApp que created successfully!');
        handleClose();
        onSuccess?.();
      } else {
        toast.error(`Failed to create que: ${response.error || response.msg}`);
      }
    } catch (error) {
      console.error('Error creating coupon whatsapp que:', error);
      toast.error('Error creating WhatsApp que. Please try again.');
    } finally {
      setSendingQue(false);
    }
  };

  const sendDisabled =
    sendingQue ||
    !eligibleUserCount ||
    (formData.headerType === 'image' && !formData.headerImage) ||
    (formData.variables.length > 0 && formData.variables.some((v) => !v.value.trim()));

  return (
    <>
      {isTemplateModalOpen && (
        <div className="whatsapp-modal-overlay coupon-whatsapp-modal-overlay">
          <div className="whatsapp-template-modal-simple">
            <div className="whatsapp-modal-header">
              <div>
                <h2 className="whatsapp-modal-title">Select Template</h2>
                <p className="whatsapp-template-subtitle">Choose a template to send messages</p>
              </div>
              <button onClick={handleClose} className="whatsapp-close-btn" type="button">
                <X size={24} />
              </button>
            </div>

            <div className="whatsapp-template-select-section">
              {loadingTemplates ? (
                <div className="whatsapp-loading-state-small">Loading templates...</div>
              ) : templates.length === 0 ? (
                <div className="whatsapp-empty-state-small">No templates available</div>
              ) : (
                <>
                  <div className="whatsapp-template-cards-grid">
                    {templates.map((template) => (
                      <div
                        key={template.name}
                        className={`whatsapp-template-card ${selectedTemplateName === template.name ? 'selected' : ''}`}
                        onClick={() => setSelectedTemplateName(template.name)}
                      >
                        <div className="whatsapp-template-card-header">
                          <h4 className="whatsapp-template-card-name">{template.name}</h4>
                          {selectedTemplateName === template.name && (
                            <div className="whatsapp-template-selected-indicator">✓</div>
                          )}
                        </div>
                        <p className="whatsapp-template-card-body">
                          {template.body?.text?.slice(0, 100) || 'No body text'}
                          {template.body?.text?.length > 100 && '...'}
                        </p>
                      </div>
                    ))}
                  </div>

                  {hasMoreTemplates && (
                    <div className="whatsapp-load-more-container">
                      <button
                        onClick={handleLoadMoreTemplates}
                        disabled={loadingMoreTemplates}
                        className="whatsapp-load-more-btn"
                        type="button"
                      >
                        {loadingMoreTemplates ? 'Loading...' : 'Load More Templates'}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="whatsapp-template-modal-footer">
              <button
                onClick={handleProceedWithTemplate}
                disabled={!selectedTemplateName || loadingTemplates}
                className={`whatsapp-proceed-btn ${!selectedTemplateName || loadingTemplates ? 'disabled' : ''}`}
                type="button"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {isMessageModalOpen && (
        <div className="whatsapp-modal-overlay coupon-whatsapp-modal-overlay">
          <div className="whatsapp-modal-content">
            <div className="whatsapp-modal-header">
              <div>
                <h2 className="whatsapp-modal-title">Send WhatsApp Message</h2>
                <div className="whatsapp-info-section">
                  <div className="whatsapp-info-box">
                    <p>• Use $name in variable for users name</p>
                    <p>• Sending to {eligibleUserCount} coupon user{eligibleUserCount !== 1 ? 's' : ''}</p>
                  </div>
                </div>
              </div>
              <button onClick={handleClose} className="whatsapp-close-btn" type="button">
                <X size={24} />
              </button>
            </div>

            <div className="whatsapp-form">
              <div className="whatsapp-form-group">
                <label>Template Name</label>
                <input
                  type="text"
                  name="templateName"
                  value={formData.templateName}
                  onChange={handleInputChange}
                  onClick={handleTemplateClick}
                  placeholder="Enter template name..."
                  className="whatsapp-input"
                  readOnly
                  style={{ cursor: 'pointer' }}
                />
              </div>

              <div className="whatsapp-section-container">
                <h3 className="whatsapp-section-title">Header</h3>

                <div className="whatsapp-radio-group">
                  {formData.headerType === 'none' && (
                    <label className="whatsapp-radio-option">
                      <input
                        type="radio"
                        name="headerType"
                        value="none"
                        checked={formData.headerType === 'none'}
                        onChange={handleInputChange}
                      />
                      <XSquare size={16} />
                      <span>None</span>
                    </label>
                  )}

                  {formData.headerType === 'text' && (
                    <label className="whatsapp-radio-option">
                      <input
                        type="radio"
                        name="headerType"
                        value="text"
                        checked={formData.headerType === 'text'}
                        onChange={handleInputChange}
                      />
                      <Type size={16} />
                      <span>Text</span>
                    </label>
                  )}

                  {formData.headerType === 'image' && (
                    <label className="whatsapp-radio-option">
                      <input
                        type="radio"
                        name="headerType"
                        value="image"
                        checked={formData.headerType === 'image'}
                        onChange={handleInputChange}
                      />
                      <Image size={16} />
                      <span>Image</span>
                    </label>
                  )}
                </div>

                {formData.headerType === 'text' && (
                  <div className="whatsapp-form-group">
                    <input
                      type="text"
                      name="headerText"
                      value={formData.headerText}
                      onChange={handleInputChange}
                      placeholder="Enter header text..."
                      className="whatsapp-input"
                    />
                  </div>
                )}

                {formData.headerType === 'image' && (
                  <div className="whatsapp-image-upload-section">
                    {!formData.headerImage ? (
                      <div className="whatsapp-image-upload-area">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageChange}
                          className="whatsapp-image-input"
                          id="coupon-header-image"
                        />
                        <label htmlFor="coupon-header-image" className="whatsapp-image-upload-label">
                          <Image size={24} />
                          <span>Click to upload header image</span>
                        </label>
                      </div>
                    ) : (
                      <div className="whatsapp-image-preview">
                        <img
                          src={URL.createObjectURL(formData.headerImage)}
                          alt="Header preview"
                          className="whatsapp-preview-image"
                        />
                        <button onClick={removeImage} className="whatsapp-remove-image-btn" type="button">
                          <Trash2 size={14} />
                          Remove
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="whatsapp-section-container">
                <h3 className="whatsapp-section-title">Body</h3>

                <div className="whatsapp-form-group">
                  <label>Text</label>
                  <textarea
                    value={templates.find((t) => t.name === formData.templateName)?.body?.text || ''}
                    className="whatsapp-textarea"
                    rows="6"
                    readOnly
                  />
                </div>

                {formData.variables.length > 0 && (
                  <div className="whatsapp-variables-section">
                    <h4 className="whatsapp-subsection-title">Variables</h4>
                    {formData.variables.map((variable, index) => (
                      <div key={variable.id} className="whatsapp-variable-item">
                        <div className="whatsapp-variable-header">
                          <label>Variable {index + 1}</label>
                        </div>
                        <input
                          type="text"
                          value={variable.value}
                          onChange={(e) => updateVariable(variable.id, e.target.value)}
                          placeholder={`{{${index + 1}}}`}
                          className="whatsapp-input"
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {formData.buttons && formData.buttons.length > 0 && (
                <div className="whatsapp-section-container">
                  <div className="whatsapp-section-header">
                    <h3 className="whatsapp-section-title">Buttons</h3>
                  </div>

                  {formData.buttons.map((button, index) => (
                    <div key={index} className="whatsapp-button-item">
                      <div className="whatsapp-button-header">
                        <label>Button {index + 1}</label>
                      </div>

                      {button.type === 'flow' ? (
                        <p className="whatsapp-note-text">Flow button is already set on the template.</p>
                      ) : (
                        <>
                          <div className="whatsapp-button-type-group">
                            <label className="whatsapp-radio-option">
                              <input
                                type="radio"
                                checked={button.type === 'url'}
                                onChange={() => updateButton(index, 'type', 'url')}
                              />
                              <Link size={16} />
                              <span>URL</span>
                            </label>

                            <label className="whatsapp-radio-option">
                              <input
                                type="radio"
                                checked={button.type === 'copy'}
                                onChange={() => updateButton(index, 'type', 'copy')}
                              />
                              <Type size={16} />
                              <span>Copy Text</span>
                            </label>
                          </div>

                          {button.type === 'copy' && (
                            <div className="whatsapp-form-group">
                              <input
                                type="text"
                                value={button.copyText || ''}
                                onChange={(e) => updateButton(index, 'copyText', e.target.value)}
                                placeholder="Text to copy..."
                                className="whatsapp-input"
                              />
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <button
                onClick={handleSend}
                disabled={sendDisabled}
                className={`whatsapp-send-btn ${sendDisabled ? 'disabled' : ''}`}
                type="button"
              >
                <Send size={18} />
                {sendingQue ? 'Creating...' : 'Send WhatsApp Message'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CreateCouponWhatsappQueModal;
